import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Footprints, Coffee, Mountain, UtensilsCrossed, Dumbbell, Lock, CalendarDays, MessageCircle, Users, Link, Zap } from 'lucide-react';
import '../styles/landing.css';

const HERO_PHOTO = 'https://images.unsplash.com/photo-1758523981764-ec58becc84da?w=1600&q=80';

const PREVIEW_CARDS = [
  { Icon: Footprints, title: 'Sunday long run', meta: 'Sun 7am · Crissy Field', going: 4 },
  { Icon: Coffee, title: 'Morning coffee catch-up', meta: 'Sat 9am · Sightglass', going: 2 },
  { Icon: Mountain, title: 'Marin headlands hike', meta: 'Sat 8am · Tennessee Valley', going: 6 },
  { Icon: UtensilsCrossed, title: 'Ramen night', meta: 'Fri 7:30pm · Japantown', going: 5 },
  { Icon: Dumbbell, title: 'Tennis anyone?', meta: 'Sat 10am · Dolores Park', going: 3 },
];

const FEATURES = [
  { Icon: Lock, title: 'Privacy first', desc: 'Your plans stay between you and your crew.' },
  { Icon: CalendarDays, title: 'Shared calendar', desc: 'See your week alongside your friends. Spot the open slot, make it happen.' },
  { Icon: MessageCircle, title: 'Activity chat', desc: 'Each activity has its own live chat thread, so last-minute changes reach people.' },
  { Icon: Users, title: 'Close Circle', desc: 'Share your free/busy time with your most trusted people. No oversharing.' },
  { Icon: Link, title: 'Invite links', desc: 'Share a preview link with anyone. They see the plan, join Covo with one tap.' },
  { Icon: Zap, title: 'No noise', desc: 'No algorithm, no ads, no engagement bait. Just real plans with real people.' },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="landing">
      {/* Nav */}
      <nav className="landing-nav">
        <span className="landing-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>COVO</span>
        <div className="landing-nav-actions">
          <button className="btn-ghost" onClick={() => navigate('/login')}>Log in</button>
          <button className="btn-cta" onClick={() => navigate('/register')}>Get started</button>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <img className="hero-photo" src={HERO_PHOTO} alt="" />
        <div className="hero-overlay-box">
          <h1>Plans with<br />your crew.</h1>
          <div className="hero-info-row">
            <div className="hero-info-col">
              <p>Share what you're doing with your people. They'll join you for more fun together.</p>
            </div>
            <div className="hero-info-col">
              <p>Never bored again, no strange meet ups.</p>
            </div>
            <button className="btn-hero-primary" onClick={() => navigate('/register')}>
              Get started →
            </button>
          </div>
        </div>
      </section>

      {/* Activity preview grid */}
      <div className="hero-preview">
        <div className="hero-preview-inner">
          {PREVIEW_CARDS.slice(0, 3).map((c, i) => (
            <div key={i} className="preview-card">
              <span className="preview-icon"><c.Icon size={20} strokeWidth={2} /></span>
              <div className="preview-card-body">
                <p className="preview-title">{c.title}</p>
                <p className="preview-meta">{c.meta}</p>
              </div>
              <div className="preview-going">
                <span className="preview-going-dot" />
                {c.going} going
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <section className="landing-features">
        <span className="section-label">Why Covo</span>
        <h2>Built for real plans,<br />facilitating spontaneity.</h2>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="feature-card">
              <span className="feature-icon"><f.Icon size={22} strokeWidth={1.8} /></span>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="landing-how">
        <span className="section-label">How it works</span>
        <h2>Three steps to a plan<br />that actually happens.</h2>
        <div className="how-steps">
          {[
            { num: '01', title: 'Post your covo', desc: 'Tell your crew what you\'re doing: a run, coffee, movie, drinks, anything. Set who can see it.' },
            { num: '02', title: 'Friends join in', desc: 'The right people RSVP. A chat thread opens. Details get sorted without the chaos.' },
            { num: '03', title: 'It actually happens', desc: 'No endless DMs. No group-chat noise. Just people showing up.' },
          ].map((s, i) => (
            <div key={i} className="how-step">
              <span className="step-num">{s.num}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta">
        <h2>Ready to make plans<br />that actually happen?</h2>
        <button className="btn-hero-primary" onClick={() => navigate('/register')}>
          Create your account →
        </button>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <span className="footer-logo">COVO</span>
        <span className="footer-note">Privacy-first. No ads. No data sold. Ever.</span>
        <div className="footer-legal-links">
          <a href="/privacy">Privacy Policy</a>
          <a href="/terms">Terms of Service</a>
        </div>
      </footer>
    </div>
  );
}
